import React, { useMemo, useState } from 'react';
import { ScrollView, useWindowDimensions } from 'react-native';
import { Image } from 'expo-image';
import { useTranslation } from 'react-i18next';
import * as AppIcon from 'expo-quick-actions/icon';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { Text } from '@/components/gluestack/text';
import { VStack } from '~/components/gluestack/vstack';
import { Pressable } from '@/components/gluestack/pressable';
import { useToastMessages } from '@/hooks/useToastMessages';
import { communities, type CommunityCode } from '@/constants/communities';
import { useAuth } from '@/components/providers/AuthProvider';

const DEFAULT_ICON = { key: null, label: 'Grokio', source: require('@/assets/icon.png') };

const NUM_COLUMNS = 3;
const ICON_GAP = 16;
const HORIZONTAL_PADDING = 12;

export default function AppIconSettings() {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const { width: screenWidth } = useWindowDimensions();
  const { getCredentials } = useAuth();

  const iconOptions = useMemo(() => {
    const communityIcons = (Object.keys(communities) as CommunityCode[])
      .filter((code) => getCredentials(code) !== null)
      .map((code) => ({
        key: code,
        label: communities[code].name,
        source: communities[code].appIcon,
      }));
    return [DEFAULT_ICON, ...communityIcons];
  }, [getCredentials]);

  const [activeIcon, setActiveIcon] = useState<string | null>(null);

  React.useEffect(() => {
    if (AppIcon.isSupported) {
      AppIcon.getIcon?.().then((name) => setActiveIcon(name ?? null));
    }
  }, []);

  const contentWidth = Math.min(screenWidth, 896) - HORIZONTAL_PADDING * 2;
  const iconSize = (contentWidth - ICON_GAP * (NUM_COLUMNS - 1)) / NUM_COLUMNS;

  const [isSwitching, setIsSwitching] = useState(false);

  const handleIconPress = async (iconKey: string | null) => {
    if (iconKey === activeIcon || isSwitching) return;

    setIsSwitching(true);
    setActiveIcon(iconKey);

    const previousIcon = activeIcon;
    try {
      await AppIcon.setIcon?.(iconKey);
      showSuccessToast(t('settings.appIcon.changed'), undefined, 'bottom');
    } catch (error) {
      setActiveIcon(previousIcon);
      showErrorToast(t('common.error.general'), error, 'bottom');
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <>
      <ScreenHeader options={{ title: t('settings.appIcon.title') }} />
      <ScrollView>
        <VStack className="mx-auto w-full max-w-4xl p-3 md:pt-0" space="md">
          <Text size="md" className="text-typography-500 px-1">
            {t('settings.appIcon.description')}
          </Text>

          <VStack
            className="flex-row flex-wrap"
            style={{ gap: ICON_GAP }}
          >
            {iconOptions.map((option) => {
              const isActive = option.key === activeIcon;
              return (
                <Pressable
                  key={option.key ?? 'default'}
                  onPress={() => handleIconPress(option.key)}
                  className="active:opacity-70"
                >
                  <VStack
                    className={`rounded-[22px] p-1 ${
                      isActive
                        ? 'border-[3px] border-primary-500'
                        : 'border border-transparent'
                    }`}
                    style={{ width: iconSize, height: iconSize }}
                  >
                    <VStack className="flex-1 rounded-[17px] overflow-hidden">
                      <Image
                        source={option.source}
                        style={{ width: '100%', height: '100%' }}
                        contentFit="cover"
                      />
                    </VStack>
                  </VStack>
                </Pressable>
              );
            })}
          </VStack>
        </VStack>
      </ScrollView>
    </>
  );
}
